import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { hasConsentDecision, saveConsent } from "../lib/consent";

const SESSION_KEY = "sws_session_id";

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Delay slightly so it doesn't jump in during initial paint
    const t = setTimeout(() => {
      if (!hasConsentDecision()) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const currentSession = () => {
    try {
      return localStorage.getItem(SESSION_KEY) || "";
    } catch {
      return "";
    }
  };

  const handle = async (choice) => {
    await saveConsent(choice, currentSession());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-[520px] z-[9999]"
    >
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--line))] shadow-2xl p-6 md:p-7 backdrop-blur">
        <div className="flex items-start gap-4">
          <div className="hidden md:flex items-center justify-center h-10 w-10 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] flex-shrink-0">
            <Cookie size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="kicker text-[hsl(var(--accent))]">Your privacy</div>
            <h2 className="font-display text-xl md:text-2xl mt-2 leading-tight">
              We use cookies to <span className="italic">personalise your visit</span>.
            </h2>
            <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
              We use strictly necessary cookies to run the site, and — with your permission —
              analytics cookies to understand which pages resonate with you. You can accept,
              reject, or change your mind any time via our{" "}
              <Link to="/cookie-policy" className="text-[hsl(var(--accent))] underline">
                Cookie Policy
              </Link>
              . Read our{" "}
              <Link to="/privacy" className="text-[hsl(var(--accent))] underline">
                Privacy Policy
              </Link>{" "}
              for full details on how we handle personal data, including your rights under the
              California CCPA.
            </p>

            {expanded && (
              <div className="mt-4 space-y-3 text-xs text-[hsl(var(--ink-soft))] border-t border-[hsl(var(--line))] pt-4">
                <div>
                  <div className="font-display text-sm text-[hsl(var(--ink))]">
                    Strictly necessary
                    <span className="ml-2 kicker text-[hsl(var(--accent))]">Always on</span>
                  </div>
                  <p className="mt-1">
                    Session identifier so the site works. Cannot be disabled.
                  </p>
                </div>
                <div>
                  <div className="font-display text-sm text-[hsl(var(--ink))]">
                    Analytics &amp; functional
                  </div>
                  <p className="mt-1">
                    Anonymous page views, scroll depth, referrer and city-level IP location so
                    we can improve the site. Loaded only if you accept.
                  </p>
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                onClick={() => handle("accepted")}
                data-testid="cookie-consent-accept"
                className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] hover:bg-[hsl(var(--accent))] transition-colors duration-500 px-6 py-3 text-[11px] tracking-[0.35em] uppercase"
              >
                Accept all
              </button>
              <button
                onClick={() => handle("rejected")}
                data-testid="cookie-consent-reject"
                className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors duration-500 px-6 py-3 text-[11px] tracking-[0.35em] uppercase"
              >
                Reject non-essential
              </button>
              <button
                onClick={() => setExpanded((v) => !v)}
                data-testid="cookie-consent-details"
                className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"
              >
                {expanded ? "Hide details" : "Details"}
              </button>
            </div>
          </div>
          <button
            onClick={() => handle("rejected")}
            aria-label="Close and reject non-essential cookies"
            className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))] flex-shrink-0"
            data-testid="cookie-consent-close"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
